import {
  Component,
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
  Show,
  For,
} from "solid-js";
import {
  DragDropProvider,
  DragDropSensors,
  DragOverlay,
  closestCenter,
} from "@thisbeyond/solid-dnd";
import type {
  CollisionDetector,
  DragEvent,
  Droppable,
} from "@thisbeyond/solid-dnd";
import { useNavigate, useSearchParams } from "@solidjs/router";
import {
  BoardColumn,
  WorkItemCard,
  QuickCreateModal,
  EpicFilterRail,
  CardContextMenu,
} from "../components/board";
import {
  Spinner,
  EmptyState,
  Button,
  SearchInput,
  MultiSelect,
} from "../components/ui";
import type { MultiSelectOption } from "../components/ui/MultiSelect";
import { TypeIcon } from "../components/work-items/TypeIcon";
import { Tooltip, useTooltips } from "../components/Tooltip";
import { useApiWithSource, useWebSocket, useToast } from "../hooks";
import { useFirstVisit } from "../utils/useFirstVisit";
import { api } from "../api";
import type {
  BoardWithColumns,
  ColumnWithStatuses,
  WorkItem,
  WorkItemType,
  CreateWorkItemRequest,
  BoardListResponse,
  WorkItemListResponse,
  EpicProgressResponse,
  BoardEvent,
} from "../api";
import { useProjectId, useProjectPrefix } from "../contexts/ProjectContext";
import { useSetChatViewContext } from "../contexts/ChatViewContext";

/**
 * BoardPage - Main Kanban board view
 *
 * Features:
 * - Fetches board and items on mount
 * - Displays columns with work items
 * - Drag-and-drop to move items between columns
 * - Optimistic updates with rollback on error
 * - Quick create inline card creation
 * - Click item to navigate to detail view
 */

/**
 * Position value that places an item at the top or bottom of an ordered
 * column. Returns null when the item is already at that edge.
 */
export const edgePosition = (
  ordered: WorkItem[],
  itemId: string,
  edge: "top" | "bottom",
): number | null => {
  if (ordered.length < 2) return null;

  if (edge === "top") {
    if (ordered[0]?.id === itemId) return null;
    return ordered[0].position - 1000;
  }

  const last = ordered[ordered.length - 1];
  if (last?.id === itemId) return null;
  return last.position + 1000;
};

/**
 * Position value for a new card occupying `index` in an ordered column
 * (0 = above the first card, ordered.length = below the last).
 * Returns null when the neighbours leave no integer gap — caller must reindex.
 */
export const insertPosition = (
  ordered: WorkItem[],
  index: number,
): number | null => {
  if (ordered.length === 0) return 0;
  if (index <= 0) return ordered[0].position - 1000;
  if (index >= ordered.length)
    return ordered[ordered.length - 1].position + 1000;
  const before = ordered[index - 1];
  const after = ordered[index];
  const gap = after.position - before.position;
  if (gap <= 1) return null;
  return Math.floor((before.position + after.position) / 2);
};

/**
 * Slot in `targetItems` (already excluding the dragged card) where a
 * cross-column drop would insert. `dropId` is the droppable under the
 * pointer: a card id inserts above that card, anything else (the column's
 * own droppable) appends.
 */
export const dropIndicatorIndex = (
  targetItems: WorkItem[],
  dropId: string,
): number => {
  const idx = targetItems.findIndex((i) => i.id === dropId);
  return idx >= 0 ? idx : targetItems.length;
};

/**
 * Translate an index into a *visible* (filtered) list of a column's items
 * into the corresponding slot in that column's *true*, unfiltered order.
 * `itemsByColumn` and `columnItemsOrdered` share the identical (position,
 * created_at) comparator, so a filtered subset preserves relative order and
 * the anchor lookup below is safe. Appends (returns `ordered.length`) when
 * the anchor is missing from `visible` (`visibleIndex` past its end) or
 * absent from `ordered` (stale render) - both mean "no anchor, go last".
 */
export const slotForVisibleIndex = (
  ordered: WorkItem[],
  visible: WorkItem[],
  visibleIndex: number,
): number => {
  const anchor = visible[visibleIndex];
  if (!anchor) return ordered.length;
  const slot = ordered.findIndex((i) => i.id === anchor.id);
  return slot < 0 ? ordered.length : slot;
};

/** True when `point` lies within `layout`, edges included. */
export const layoutContainsPoint = (
  layout: Droppable["layout"],
  point: Droppable["layout"]["center"],
): boolean =>
  point.x >= layout.left &&
  point.x <= layout.right &&
  point.y >= layout.top &&
  point.y <= layout.bottom;

/**
 * Collision detector for the board: the column whose drop area contains the
 * dragged card's centre owns the drop, and the slot within it is resolved by
 * `closestCenter` over that column's own droppables.
 *
 * Plain `closestCenter` over every droppable loses the top band of an *empty*
 * column. It scores centre-to-centre only, and a column's drop area is
 * `flex-1`, so its centre sits half a column-height below its top edge, while
 * a card in the next column is only a column-pitch away. Once the column is
 * taller than twice that pitch the vertical gap wins, and the top
 * `height / 2 - pitch` pixels of an empty column stop lighting up (FLOW-813).
 * Populated columns hide it: their own cards cover the same band.
 *
 * Scoping the candidates to one column removes that cross-column interference
 * and leaves within-column behaviour intact — over a card the card still wins,
 * in the gap between two cards the nearer one still wins, below the last card
 * the column still wins. With the centre over no column at all (the board
 * gutters) the old whole-board behaviour stands. One deliberate change beyond
 * the bug: a card scrolled out of its column's visible box has its centre
 * outside the column rect, so it is no longer a candidate — you cannot target
 * what you cannot see.
 */
export const columnScopedCollisionDetector =
  (isColumnId: (id: Droppable["id"]) => boolean): CollisionDetector =>
  (draggable, droppables, context) => {
    const centre = draggable.transformed.center;
    const column = droppables.find(
      (d) => isColumnId(d.id) && layoutContainsPoint(d.layout, centre),
    );
    if (!column) return closestCenter(draggable, droppables, context);
    return closestCenter(
      draggable,
      droppables.filter(
        (d) =>
          d.id === column.id ||
          layoutContainsPoint(column.layout, d.layout.center),
      ),
      context,
    );
  };

export const BoardPage: Component = () => {
  const navigate = useNavigate();
  const projectId = useProjectId();

  const { showError, showSuccess } = useToast();

  const setViewContext = useSetChatViewContext();

  createEffect(() => {
    // Don't update the chat context while items are still loading for the new
    // project — doing so would write stale IDs from the previous project into
    // the view context before the fresh list arrives.
    if (loadingItems()) return;
    setViewContext({
      type: "backlog",
      filters: { typeIds: [], statusIds: [], search: "" },
      visibleItemIds: items().map((i) => i.id),
      selectedItemIds: [],
      totalItemCount: items().length,
    });
  });

  onCleanup(() => setViewContext({ type: "general" }));

  const tooltips = useTooltips();
  const isFirstVisit = useFirstVisit("board");
  const [firstColumnHeaderRef, setFirstColumnHeaderRef] =
    createSignal<HTMLElement | null>(null);
  const [showBoardTooltip, setShowBoardTooltip] = createSignal(false);

  const [quickCreateColumn, setQuickCreateColumn] = createSignal<{
    columnId: string;
    statusId: string;
    columnName: string;
    insertIndex: number | null;
  } | null>(null);
  const [isCreating, setIsCreating] = createSignal(false);

  const [activeItem, setActiveItem] = createSignal<WorkItem | null>(null);
  // Track which column is the current drop target (for visual feedback)
  const [activeDropColumnId, setActiveDropColumnId] = createSignal<
    string | null
  >(null);
  // Track which slot within the target column the card would land in (for
  // cross-column drops only — same-column drags already get a visual cue
  // for free from solid-dnd's sortable transform).
  const [activeDropIndex, setActiveDropIndex] = createSignal<number | null>(
    null,
  );

  // Card context menu ("Send to top" / "Send to bottom")
  const [contextMenu, setContextMenu] = createSignal<{
    item: WorkItem;
    x: number;
    y: number;
    trigger: HTMLElement | null;
  } | null>(null);

  /**
   * Close the context menu and, if it was opened via the "⋯" trigger button
   * (not a right-click, which has no persistent focus target), restore focus
   * to that button.
   */
  const closeContextMenu = () => {
    const trigger = contextMenu()?.trigger;
    setContextMenu(null);
    if (trigger) {
      requestAnimationFrame(() => trigger.focus());
    }
  };

  const {
    data: boardsResponse,
    loading: loadingBoards,
    error: boardsError,
  } = useApiWithSource(projectId, (pid) =>
    api.get<BoardListResponse>(`/projects/${pid}/boards`),
  );

  const defaultBoardId = createMemo(() => {
    const response = boardsResponse();
    if (!response || response.boards.length === 0) return null;
    const defaultBoard =
      response.boards.find((b) => b.is_default) ?? response.boards[0];
    return defaultBoard.id;
  });

  const {
    data: board,
    loading: loadingBoard,
    error: boardError,
    refetch: refetchBoard,
  } = useApiWithSource(defaultBoardId, (boardId) => {
    if (!boardId) return Promise.resolve(null);
    return api.get<BoardWithColumns>(`/boards/${boardId}`);
  });

  // Get the board ID for items fetch (need a stable accessor)
  const boardId = createMemo(() => board()?.id ?? null);

  const [filterText, setFilterText] = createSignal("");
  const prefix = useProjectPrefix();

  const [selectedEpicId, setSelectedEpicId] = createSignal<string | null>(null);

  // Combined source: refetch items when board or selected epic changes
  const boardItemsSource = createMemo(() => {
    const id = boardId();
    if (!id) return null;
    return { boardId: id, epicId: selectedEpicId() };
  });

  // Fetch items for the board (filtered by epic when selected)
  const {
    data: itemsResponse,
    loading: loadingItems,
    error: itemsError,
    refetch: refetchItems,
    mutate: mutateItemsResponse,
  } = useApiWithSource(boardItemsSource, (source) => {
    if (!source) return Promise.resolve({ items: [], count: 0 });
    const params = source.epicId ? `?epic_id=${source.epicId}` : "";
    return api.get<WorkItemListResponse>(
      `/boards/${source.boardId}/items${params}`,
    );
  });

  const items = createMemo(() => itemsResponse()?.items ?? []);

  const mutateItems = (newItems: WorkItem[]) => {
    mutateItemsResponse({ items: newItems, count: newItems.length });
  };

  /**
   * Handle WebSocket events for real-time updates
   */
  const handleWebSocketMessage = (event: BoardEvent) => {
    // Only process events for the current project
    if (event.payload.project_id !== projectId()) return;

    const currentItems = items();

    switch (event.type) {
      case "WorkItemCreated": {
        // Add new item if not already present (avoid duplicates from optimistic updates)
        const exists = currentItems.some((i) => i.id === event.payload.item.id);
        if (!exists) {
          mutateItems([...currentItems, event.payload.item]);
        }
        refetchEpics();
        break;
      }
      case "WorkItemUpdated": {
        mutateItems(
          currentItems.map((i) =>
            i.id === event.payload.item.id ? event.payload.item : i,
          ),
        );
        break;
      }
      case "WorkItemStatusChanged": {
        mutateItems(
          currentItems.map((i) =>
            i.id === event.payload.item_id ? event.payload.item : i,
          ),
        );
        refetchEpics();
        break;
      }
      case "WorkItemDeleted": {
        mutateItems(currentItems.filter((i) => i.id !== event.payload.item_id));
        refetchEpics();
        break;
      }
      case "StatusesChanged": {
        // The event carries only the project id: someone changed the project's
        // statuses, and creating one also creates a column (FLOW-575), so the
        // columns have to be refetched alongside the items rather than patched.
        refetchBoard();
        refetchItems();
        break;
      }
    }
  };

  // Events broadcast while the socket was down are never delivered, so the
  // cards can be stale by an unbounded amount once it comes back (FLOW-964).
  // Not a signal — it drives no rendering, only which branch the next status
  // transition takes, and so distinguishes a reconnect from the first connect
  // of a page load, whose data was just fetched.
  let sawDisconnect = false;

  const { status: wsStatus } = useWebSocket({
    onMessage: handleWebSocketMessage,
    onError: (e) => {
      console.error("WebSocket error:", e);
    },
    onStatusChange: (status) => {
      if (status === "disconnected") {
        sawDisconnect = true;
        return;
      }
      if (status === "connected" && sawDisconnect) {
        sawDisconnect = false;
        // Columns as well as items: a StatusesChanged broadcast missed while
        // the socket was down also adds columns (FLOW-575).
        refetchBoard();
        refetchItems();
        refetchEpics();
      }
    },
  });

  // Fetch work item types for the project (returns raw array)
  const { data: itemTypes } = useApiWithSource(projectId, (pid) =>
    api.get<WorkItemType[]>(`/projects/${pid}/work-items/types`),
  );

  const [searchParams, setSearchParams] = useSearchParams<{ type?: string }>();
  const [selectedTypeIds, setSelectedTypeIds] = createSignal<string[]>([]);
  let urlHydrated = false;

  createEffect(() => {
    if (urlHydrated) return;
    const types = itemTypes();
    if (!types || types.length === 0) return;
    const raw = searchParams.type;
    if (raw) {
      const ids = raw
        .split(",")
        .filter(Boolean)
        .map(
          (name) =>
            types.find((t) => t.name.toLowerCase() === name.toLowerCase())?.id,
        )
        .filter((id): id is string => !!id);
      setSelectedTypeIds(ids);
    }
    urlHydrated = true;
  });

  const typeOptions = createMemo((): MultiSelectOption[] =>
    (itemTypes() ?? []).map((t) => ({
      value: t.id,
      label: t.name,
      icon: <TypeIcon typeName={t.name} />,
    })),
  );

  const handleTypeChange = (ids: string[]) => {
    setSelectedTypeIds(ids);
    const types = itemTypes() ?? [];
    const typeNames = ids
      .map((id) => types.find((t) => t.id === id)?.name.toLowerCase())
      .filter(Boolean)
      .join(",");
    setSearchParams({ type: typeNames || undefined }, { replace: true });
  };

  const {
    data: epicProgressResponse,
    loading: loadingEpics,
    refetch: refetchEpics,
  } = useApiWithSource(projectId, (pid) =>
    api.get<EpicProgressResponse>(`/projects/${pid}/epics/progress`),
  );

  const epics = createMemo(() => epicProgressResponse()?.epics ?? []);

  // Compute hierarchy data: child counts and parent titles from existing items
  const hierarchy = createMemo(() => {
    const childCounts: Record<string, number> = {};
    const parentTitles: Record<string, string> = {};
    for (const item of items()) {
      parentTitles[item.id] = item.title;
      if (item.parent_id) {
        childCounts[item.parent_id] = (childCounts[item.parent_id] ?? 0) + 1;
      }
    }
    return { childCounts, parentTitles };
  });

  // Group items by column based on status_ids mapping, sorted by position
  const itemsByColumn = createMemo(() => {
    const b = board();
    const allItems = items();
    const grouped: Record<string, WorkItem[]> = {};

    if (!b) return grouped;

    const query = filterText().toLowerCase().trim();
    const pfx = prefix();
    const typeIds = selectedTypeIds();

    for (const column of b.columns) {
      grouped[column.id] = allItems
        .filter((item) => {
          if (!column.status_ids.includes(item.status_id)) return false;
          if (typeIds.length > 0 && !typeIds.includes(item.item_type_id))
            return false;
          if (!query) return true;
          const displayId = `${pfx}-${item.item_number}`.toLowerCase();
          return (
            item.title.toLowerCase().includes(query) ||
            displayId.includes(query)
          );
        })
        .sort(
          (a, b) =>
            a.position - b.position || a.created_at.localeCompare(b.created_at),
        );
    }

    return grouped;
  });

  const isLoading = () =>
    loadingBoards() || loadingBoard() || (board() !== null && loadingItems());

  const error = () => boardsError() || boardError() || itemsError();

  // Timeout state - show timeout screen after 15s without board data
  const [timedOut, setTimedOut] = createSignal(false);
  const timeoutTimer = setTimeout(() => setTimedOut(true), 15_000);
  onCleanup(() => clearTimeout(timeoutTimer));
  createEffect(() => {
    if (board()) {
      clearTimeout(timeoutTimer);
      setTimedOut(false);
    }
  });

  // Tooltip setup - show board tooltip on first visit
  createEffect(() => {
    if (
      isFirstVisit() &&
      !tooltips.isDismissed("board-columns") &&
      board() &&
      !isLoading()
    ) {
      // Wait a bit for the DOM to render, then find the first column header
      const timer = setTimeout(() => {
        const firstHeader = document.querySelector(
          ".flex.items-center.gap-2.min-w-0",
        ) as HTMLElement;
        if (firstHeader) {
          setFirstColumnHeaderRef(firstHeader);
          setShowBoardTooltip(true);
        }
      }, 500);
      onCleanup(() => clearTimeout(timer));
    }
  });

  const handleDismissBoardTooltip = () => {
    tooltips.dismiss("board-columns");
    setShowBoardTooltip(false);
  };

  /**
   * Handle drag start - store active item for overlay
   */
  const handleDragStart = (event: DragEvent) => {
    const itemId = event.draggable.id as string;
    const item = items().find((i) => i.id === itemId);
    if (item) {
      setActiveItem(item);
    }
    setActiveDropIndex(null);
  };

  /**
   * Handle drag over - track which column is the current drop target, and
   * (for cross-column drags) which slot within it the card would land in.
   * Same-column drags leave the index null: solid-dnd's sortable transform
   * already opens a visible gap there, and a second cue would fight it.
   */
  const handleDragOver = (event: DragEvent) => {
    const { droppable } = event;
    if (!droppable) {
      setActiveDropColumnId(null);
      setActiveDropIndex(null);
      return;
    }
    const b = board();
    if (!b) {
      setActiveDropColumnId(null);
      setActiveDropIndex(null);
      return;
    }

    const dropId = droppable.id as string;

    const setDropIndexForTarget = (targetCol: ColumnWithStatuses) => {
      const dragId = event.draggable.id as string;
      const dragItem = items().find((i) => i.id === dragId);
      const sourceCol = dragItem ? findColumnForItem(dragItem) : undefined;
      if (!dragItem || sourceCol?.id === targetCol.id) {
        setActiveDropIndex(null);
        return;
      }
      const targetItems = (itemsByColumn()[targetCol.id] ?? []).filter(
        (i) => i.id !== dragId,
      );
      setActiveDropIndex(dropIndicatorIndex(targetItems, dropId));
    };

    const col = b.columns.find((c) => c.id === dropId);
    if (col) {
      setActiveDropColumnId(col.id);
      setDropIndexForTarget(col);
      return;
    }
    // Otherwise it's a card - find which column it belongs to
    const card = items().find((i) => i.id === dropId);
    if (card) {
      const cardCol = findColumnForItem(card);
      setActiveDropColumnId(cardCol?.id ?? null);
      if (cardCol) {
        setDropIndexForTarget(cardCol);
      } else {
        setActiveDropIndex(null);
      }
    }
  };

  /**
   * Find which column a work item belongs to based on its status_id
   */
  const findColumnForItem = (
    item: WorkItem,
  ): ColumnWithStatuses | undefined => {
    return board()?.columns.find((c) => c.status_ids.includes(item.status_id));
  };

  /**
   * All of a column's items in position order, ignoring the client-side
   * search text / type filters — "send to top/bottom" is relative to the
   * whole column, not just the currently visible subset. The epic filter is
   * different: it's server-side (see `boardItemsSource` above), so when the
   * epic rail has a selection `items()` is already scoped to that epic and
   * "top/bottom" means the top/bottom of that epic's cards within the
   * column. `handleDragEnd` reads the same epic-scoped `items()`, so the two
   * share that limitation — but not the rest: it splices `itemsByColumn()`,
   * which *does* apply the search/type filters this ignores.
   */
  const columnItemsOrdered = (col: ColumnWithStatuses) =>
    items()
      .filter((i) => col.status_ids.includes(i.status_id))
      .sort(
        (a, b) =>
          a.position - b.position || a.created_at.localeCompare(b.created_at),
      );

  /**
   * Handle right-click / "Card actions" click on a card - open the context menu.
   */
  const handleCardContextMenu = (
    item: WorkItem,
    x: number,
    y: number,
    trigger: HTMLElement | null,
  ) => {
    setContextMenu({ item, x, y, trigger });
  };

  /**
   * Send a card to the top or bottom of its column.
   */
  const handleSendTo = async (item: WorkItem, edge: "top" | "bottom") => {
    const col = findColumnForItem(item);
    if (!col) return;

    const newPosition = edgePosition(columnItemsOrdered(col), item.id, edge);
    if (newPosition === null) return;

    const currentItems = items();
    const oldItems = currentItems.map((i) => ({ ...i }));
    mutateItems(
      currentItems.map((i) =>
        i.id === item.id ? { ...i, position: newPosition } : i,
      ),
    );

    try {
      await api.patch(`/work-items/${item.id}`, { position: newPosition });
    } catch (e) {
      mutateItems(oldItems);
      showError(
        `Failed to move item: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    }
  };

  /**
   * Compute new position value for an item at a given index in the new order.
   * Looks at neighbors to find a midpoint. If the gap is too small or items
   * all share the same position, returns null to signal a full reindex is needed.
   */
  const computePosition = (
    orderedItems: WorkItem[],
    itemIndex: number,
  ): number | null => {
    const before = itemIndex > 0 ? orderedItems[itemIndex - 1] : null;
    const after =
      itemIndex < orderedItems.length - 1 ? orderedItems[itemIndex + 1] : null;

    if (!before && !after) return 0;
    if (!before) return (after?.position ?? 0) - 1000;
    if (!after) return before.position + 1000;

    const gap = after.position - before.position;
    if (gap <= 1) return null; // Not enough room, need reindex
    return Math.floor((before.position + after.position) / 2);
  };

  /**
   * Space a column's items at i*1000 so an insertion slot exists. Returns the
   * respaced order. Follows the `handleSendTo` pattern: snapshots `oldItems`
   * before the optimistic update and rolls back if any PATCH rejects, and
   * throws a reindex-specific error so the caller doesn't misreport this as
   * whatever operation triggered the reindex.
   */
  const reindexColumn = async (ordered: WorkItem[]): Promise<WorkItem[]> => {
    const respaced = ordered.map((item, i) => ({
      ...item,
      position: i * 1000,
    }));
    const changed = respaced.filter(
      (item, i) => item.position !== ordered[i].position,
    );
    const oldItems = items().map((i) => ({ ...i }));
    mutateItems(items().map((i) => respaced.find((r) => r.id === i.id) ?? i));
    try {
      await Promise.all(
        changed.map((item) =>
          api.patch(`/work-items/${item.id}`, { position: item.position }),
        ),
      );
    } catch (e) {
      mutateItems(oldItems);
      throw new Error(
        `Failed to reindex column: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    }
    return respaced;
  };

  /**
   * Handle drag end - reorder within column or move between columns.
   *
   * Uses the standard solid-dnd sortable pattern: find the source and target
   * indices, splice the array to produce the new order, then persist.
   * All item copies are immutable (spread) to avoid mutating shared references.
   */
  const handleDragEnd = async (event: DragEvent) => {
    setActiveItem(null);
    setActiveDropColumnId(null);
    setActiveDropIndex(null);

    const { draggable, droppable } = event;
    if (!draggable || !droppable) return;

    const dragId = draggable.id as string;
    const dropId = droppable.id as string;
    const b = board();
    if (!b) return;

    const currentItems = items();
    const dragItem = currentItems.find((i) => i.id === dragId);
    if (!dragItem) return;

    // Determine if drop target is a column droppable or a card sortable
    const dropColumn = b.columns.find((c) => c.id === dropId);
    const dropCard = !dropColumn
      ? currentItems.find((i) => i.id === dropId)
      : undefined;

    // Determine target column
    let targetCol: ColumnWithStatuses | undefined;
    if (dropColumn) {
      targetCol = dropColumn;
    } else if (dropCard) {
      targetCol = findColumnForItem(dropCard);
    }
    if (!targetCol) return;

    const targetStatusId = targetCol.status_ids[0];
    if (!targetStatusId) return;

    const sourceCol = findColumnForItem(dragItem);
    const isSameColumn = sourceCol?.id === targetCol.id;

    // Build the new column order using the standard sortable splice pattern.
    // For same-column: use original list with both items, find from/to, splice.
    // For cross-column: remove from source, insert into target at drop position.
    let newColumnOrder: WorkItem[];

    if (isSameColumn && dropCard) {
      const originalItems = itemsByColumn()[targetCol.id] ?? [];
      const fromIndex = originalItems.findIndex((i) => i.id === dragId);
      const toIndex = originalItems.findIndex((i) => i.id === dropId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

      newColumnOrder = originalItems.map((i) => ({ ...i }));
      const [moved] = newColumnOrder.splice(fromIndex, 1);
      newColumnOrder.splice(toIndex, 0, moved);
    } else {
      const targetItems = (itemsByColumn()[targetCol.id] ?? []).filter(
        (i) => i.id !== dragId,
      );

      let insertIndex: number;
      if (dropColumn) {
        insertIndex = targetItems.length;
      } else if (dropCard) {
        const dropIdx = targetItems.findIndex((i) => i.id === dropId);
        insertIndex = dropIdx >= 0 ? dropIdx : targetItems.length;
      } else {
        insertIndex = targetItems.length;
      }

      newColumnOrder = targetItems.map((i) => ({ ...i }));
      newColumnOrder.splice(insertIndex, 0, { ...dragItem });
    }

    const movedIndex = newColumnOrder.findIndex((i) => i.id === dragId);
    if (movedIndex < 0) return;

    // Compute position for the moved item based on its new neighbors
    const computedPosition = computePosition(newColumnOrder, movedIndex);

    // Reindex if position computation failed (no gap) or all items share same position
    const reindex =
      computedPosition === null ||
      (newColumnOrder.length > 1 &&
        newColumnOrder.every((i) => i.position === newColumnOrder[0].position));

    // Record original positions before any changes (for reindex API calls)
    const originalPositions = new Map(
      newColumnOrder.map((i) => [i.id, i.position]),
    );

    let newPosition: number;
    if (reindex) {
      // Assign evenly-spaced positions to all items in the column
      newColumnOrder.forEach((item, i) => {
        item.position = i * 1000;
      });
      newPosition = newColumnOrder[movedIndex].position;
    } else {
      newPosition = computedPosition;
      newColumnOrder[movedIndex].position = newPosition;
    }

    // Set the moved item's status (relevant for cross-column moves)
    newColumnOrder[movedIndex].status_id = targetStatusId;

    // Deep copy current items for rollback (each item is a new object)
    const oldItems = currentItems.map((i) => ({ ...i }));

    // Optimistic update - apply new column order to global items list
    const updatedItems = currentItems.map((item) => {
      const updated = newColumnOrder.find((u) => u.id === item.id);
      if (updated) return updated;
      return item;
    });
    mutateItems(updatedItems);

    try {
      if (!isSameColumn) {
        await api.post(`/work-items/${dragId}/status`, {
          status_id: targetStatusId,
          position: newPosition,
        });
      } else {
        await api.patch(`/work-items/${dragId}`, {
          position: newPosition,
        });
      }

      // If we reindexed, update positions for other items that changed
      if (reindex) {
        const otherUpdates = newColumnOrder
          .filter((item) => item.id !== dragId)
          .filter((item) => {
            const origPos = originalPositions.get(item.id);
            return origPos !== undefined && origPos !== item.position;
          })
          .map((item) =>
            api.patch(`/work-items/${item.id}`, { position: item.position }),
          );
        if (otherUpdates.length > 0) {
          await Promise.all(otherUpdates);
        }
      }

      // Refresh epic progress after cross-column status change
      if (!isSameColumn) {
        refetchEpics();
      }
    } catch (e) {
      mutateItems(oldItems);
      showError(
        `Failed to move item: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    }
  };

  /**
   * Reads `board()` on each call rather than closing over a snapshot: the
   * detector is built once (the component runs once) but runs on every move.
   */
  const boardCollisionDetector = columnScopedCollisionDetector((id) =>
    (board()?.columns ?? []).some((c) => c.id === id),
  );

  /**
   * Handle item click - navigate to detail view
   */
  const handleItemClick = (item: WorkItem) => {
    navigate(`/items/${item.id}?from=board`);
  };

  /**
   * Handle add item click - show quick create modal
   */
  const handleAddItem = (columnId: string, statusId: string) => {
    const column = board()?.columns.find((c) => c.id === columnId);
    setQuickCreateColumn({
      columnId,
      statusId,
      columnName: column?.name ?? "New",
      insertIndex: null,
    });
  };

  /**
   * Handle clicking the dashed-plus divider above a card - show quick create
   * modal targeting the slot at `visibleIndex` in the column's true order.
   */
  const handleAddItemAt = (
    columnId: string,
    statusId: string,
    visibleIndex: number,
  ) => {
    const column = board()?.columns.find((c) => c.id === columnId);
    const ordered = column ? columnItemsOrdered(column) : [];
    const visible = itemsByColumn()[columnId] ?? [];
    setQuickCreateColumn({
      columnId,
      statusId,
      columnName: column?.name ?? "New",
      insertIndex: slotForVisibleIndex(ordered, visible, visibleIndex),
    });
  };

  // Derive selected epic title for the quick-create modal context
  const selectedEpicTitle = createMemo(() => {
    const epicId = selectedEpicId();
    if (!epicId) return undefined;
    return epics().find((e) => e.id === epicId)?.title;
  });

  /**
   * Handle quick create submit
   */
  const handleQuickCreate = async (data: CreateWorkItemRequest) => {
    setIsCreating(true);
    try {
      // Resolve the position for a divider-triggered create, reindexing the
      // column first if ties leave no integer gap for the slot.
      const slot = quickCreateColumn()?.insertIndex ?? null;
      let position: number | undefined;
      if (slot !== null) {
        const column = board()?.columns.find(
          (c) => c.id === quickCreateColumn()?.columnId,
        );
        let ordered = column ? columnItemsOrdered(column) : [];
        let computed = insertPosition(ordered, slot);
        if (computed === null) {
          if (selectedEpicId()) {
            // reindexColumn only ever sees items(), which is server-side
            // scoped to the selected epic (see columnItemsOrdered's doc
            // comment). Respacing that epic-scoped subset here would leave
            // the rest of the column's true order untouched, silently
            // corrupting it the moment the epic filter clears. Skip the
            // reindex while an epic filter is active; `computed` stays
            // null, no position is sent, and the server places the card at
            // the top of the column (FLOW-120).
          } else {
            try {
              ordered = await reindexColumn(ordered);
            } catch (e) {
              showError(
                e instanceof Error ? e.message : "Failed to reindex column",
              );
              return;
            }
            computed = insertPosition(ordered, slot);
          }
        }
        position = computed ?? undefined;
      }

      // Auto-assign to the active epic when an epic filter is selected
      const epicId = selectedEpicId();
      const payload = {
        ...data,
        ...(epicId ? { parent_id: epicId } : {}),
        ...(position !== undefined ? { position } : {}),
      };
      await api.post<WorkItem>(`/projects/${projectId()}/work-items`, payload);
      // Don't add locally - WebSocket broadcasts to all tabs including this one
      setQuickCreateColumn(null);
      showSuccess("Item created successfully");
    } catch (e) {
      showError(
        `Failed to create item: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Handle quick create cancel
   */
  const handleQuickCreateCancel = () => {
    setQuickCreateColumn(null);
  };

  return (
    <div class="h-full flex flex-col">
      {/* Board Header */}
      <div class="flex items-center justify-between h-11 px-4 border-b border-line bg-surface">
        <div class="flex items-center gap-3">
          <h1 class="text-lg font-semibold text-ink">
            {board()?.name ?? "Board"}
          </h1>
          <Show when={board()}>
            <span class="font-mono text-xs tabular-nums text-ink-3">
              <Show
                when={filterText() || selectedTypeIds().length > 0}
                fallback={<>{items().length} items</>}
              >
                {Object.values(itemsByColumn()).reduce(
                  (sum, col) => sum + col.length,
                  0,
                )}{" "}
                of {items().length} items
              </Show>
            </span>
          </Show>
        </div>
        <div class="flex items-center gap-2">
          <SearchInput
            value={filterText()}
            placeholder="Filter by title or ID..."
            onSearch={setFilterText}
            debounceMs={150}
            class="w-48"
          />
          <MultiSelect
            options={typeOptions()}
            selected={selectedTypeIds()}
            placeholder="Type"
            onChange={handleTypeChange}
            class="w-[140px]"
          />
          <Show when={selectedTypeIds().length > 0}>
            <button
              type="button"
              class="h-7 px-2 -mx-2 text-sm text-ink-3 hover:text-ink transition-colors whitespace-nowrap focus-ring rounded-sm"
              onClick={() => handleTypeChange([])}
              aria-label="Clear type filter"
            >
              Clear
            </button>
          </Show>
          {/* WebSocket connection status indicator */}
          <div
            class="flex items-center gap-1.5 text-xs"
            title={`Real-time updates: ${wsStatus()}`}
          >
            <div
              class={`w-2 h-2 rounded-full ${
                wsStatus() === "connected"
                  ? "bg-signal-green"
                  : wsStatus() === "connecting"
                    ? "bg-signal-amber animate-pulse"
                    : "bg-line-strong"
              }`}
            />
            <span class="text-ink-3 hidden sm:inline">
              {wsStatus() === "connected"
                ? "Live"
                : wsStatus() === "connecting"
                  ? "Connecting..."
                  : "Offline"}
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetchItems()}
            disabled={isLoading()}
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </Button>
        </div>
      </div>

      {/* Epic Filter Rail */}
      <EpicFilterRail
        epics={epics()}
        selectedEpicId={selectedEpicId()}
        onSelect={setSelectedEpicId}
        loading={loadingEpics() && !epicProgressResponse()}
        columns={board()?.columns}
      />

      {/* Board Content */}
      <div class="flex-1 overflow-hidden">
        <Show
          when={!timedOut() || board()}
          fallback={
            <div class="h-full flex items-center justify-center p-8">
              <EmptyState
                icon={
                  <svg
                    class="w-12 h-12"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                }
                title="Unable to reach the server"
                description="The board is taking longer than expected to load. The backend may be unavailable."
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </Button>
                }
              />
            </div>
          }
        >
          <Show
            when={!isLoading()}
            fallback={
              <div class="h-full flex items-center justify-center">
                <div class="flex flex-col items-center">
                  <Spinner size="lg" />
                  <p class="mt-4 text-sm text-ink-3">Loading board...</p>
                </div>
              </div>
            }
          >
            <Show
              when={!error()}
              fallback={
                <div class="h-full flex items-center justify-center p-8">
                  <EmptyState
                    icon={
                      <svg
                        class="w-12 h-12"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    }
                    title="Error loading board"
                    description={
                      error()?.message ?? "An unexpected error occurred"
                    }
                    action={
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.location.reload()}
                      >
                        Reload page
                      </Button>
                    }
                  />
                </div>
              }
            >
              <Show
                when={board()}
                fallback={
                  <div class="h-full flex items-center justify-center p-8">
                    <EmptyState
                      icon={
                        <svg
                          class="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="1.5"
                            d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                          />
                        </svg>
                      }
                      title="No board found"
                      description="Create a board to get started with your project"
                    />
                  </div>
                }
              >
                {/* Kanban Board with Drag and Drop */}
                <DragDropProvider
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  collisionDetector={boardCollisionDetector}
                >
                  <DragDropSensors />

                  <div
                    data-board-scroller
                    class="h-full overflow-x-auto overflow-y-hidden p-3 bg-paper"
                  >
                    <div class="flex gap-3 h-full">
                      <For
                        each={board()?.columns.sort(
                          (a: { position: number }, b: { position: number }) =>
                            a.position - b.position,
                        )}
                      >
                        {(column) => (
                          <BoardColumn
                            column={column}
                            items={itemsByColumn()[column.id] ?? []}
                            itemTypes={itemTypes() ?? []}
                            childCounts={hierarchy().childCounts}
                            parentTitles={hierarchy().parentTitles}
                            isDropTarget={activeDropColumnId() === column.id}
                            dropIndex={
                              activeDropColumnId() === column.id
                                ? activeDropIndex()
                                : null
                            }
                            openMenuItemId={contextMenu()?.item.id}
                            onItemClick={handleItemClick}
                            onItemContextMenu={handleCardContextMenu}
                            onAddItem={handleAddItem}
                            onAddItemAt={handleAddItemAt}
                            dragging={activeItem() !== null}
                          />
                        )}
                      </For>
                    </div>
                  </div>

                  {/* Drag Overlay - Shows card while dragging */}
                  <DragOverlay>
                    <Show when={activeItem()}>
                      {(item) => {
                        const parentId = item().parent_id;
                        return (
                          <div class="w-[240px] opacity-90 rotate-2 shadow-overlay rounded">
                            <WorkItemCard
                              item={item()}
                              overlay
                              itemType={itemTypes()?.find(
                                (t) => t.id === item().item_type_id,
                              )}
                              childCount={hierarchy().childCounts[item().id]}
                              parentTitle={
                                parentId
                                  ? hierarchy().parentTitles[parentId]
                                  : undefined
                              }
                            />
                          </div>
                        );
                      }}
                    </Show>
                  </DragOverlay>

                  <Show when={contextMenu()}>
                    {(menu) => {
                      const orderedColumnItems = createMemo(() => {
                        const c = findColumnForItem(menu().item);
                        return c ? columnItemsOrdered(c) : null;
                      });
                      const edgeFor = (edge: "top" | "bottom") => {
                        const ordered = orderedColumnItems();
                        if (!ordered) return null;
                        return edgePosition(ordered, menu().item.id, edge);
                      };
                      return (
                        <CardContextMenu
                          x={menu().x}
                          y={menu().y}
                          disableTop={edgeFor("top") === null}
                          disableBottom={edgeFor("bottom") === null}
                          onSendToTop={() =>
                            void handleSendTo(menu().item, "top")
                          }
                          onSendToBottom={() =>
                            void handleSendTo(menu().item, "bottom")
                          }
                          onClose={closeContextMenu}
                        />
                      );
                    }}
                  </Show>
                </DragDropProvider>
              </Show>
            </Show>
          </Show>
        </Show>
      </div>

      {/* Quick Create Modal */}
      <QuickCreateModal
        open={quickCreateColumn() !== null}
        onClose={handleQuickCreateCancel}
        projectId={projectId()}
        statusId={quickCreateColumn()?.statusId ?? ""}
        columnName={quickCreateColumn()?.columnName}
        epicTitle={selectedEpicTitle()}
        itemTypes={itemTypes() ?? []}
        loading={isCreating()}
        onCreate={handleQuickCreate}
      />

      {/* Feature Discovery Tooltip */}
      <Tooltip
        anchor={firstColumnHeaderRef()}
        content="Drag cards between columns to update their status. Click a column header to rename it."
        id="board-columns"
        onDismiss={handleDismissBoardTooltip}
        show={showBoardTooltip()}
      />
    </div>
  );
};

export default BoardPage;
